import { useCallback, useEffect, useMemo, useState } from "react"

import styles from "./RereservationStatusFixModal.module.scss"

import { graphql } from "@/company_admin/gql/__generated__"
import type {
	RereservationConfirmationStatus,
	RereservationStatusInput,
} from "@/company_admin/gql/__generated__/graphql"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import {
	useCompanyAdminMutation,
	useCompanyAdminQuery,
} from "@/provider/apollo/Apollo"
import { formatIsoDateTime, formatYearMonth } from "@/utils/date"
import { formatAmount } from "@/utils/money"

// --- GraphQL ---

const GetSettlementRereservations = graphql(/* GraphQL */ `
	query GetSettlementRereservations($param: SettlementRereservationsParam!) {
		settlementRereservations(param: $param) {
			cancelId
			salonId
			amount
			startTime
			customerName
			staffName
			status
		}
	}
`)

const ManuallyUpdateRereservationStatuses = graphql(/* GraphQL */ `
	mutation ManuallyUpdateRereservationStatuses(
		$input: ManuallyUpdateRereservationStatusesInput!
	) {
		manuallyUpdateRereservationStatuses(input: $input) {
			success
		}
	}
`)

type StatusOption = {
	label: string
	value: RereservationStatusInput
}

const statusOptions: StatusOption[] = [
	{ label: "来店済", value: "VISITED" },
	{ label: "キャンセル", value: "CANCELLED" },
	{ label: "未確定", value: "UNCONFIRMED" },
]

/** RereservationConfirmationStatus (output) を RereservationStatusInput に変換 */
const toStatusInput = (
	s: RereservationConfirmationStatus
): RereservationStatusInput => {
	switch (s) {
		case "CANCELLED":
			return "CANCELLED"
		case "VISITED":
			return "VISITED"
		case "UNCONFIRMED":
			return "UNCONFIRMED"
	}
}

type Props = {
	onUpdateComplete: () => void
}

export const useRereservationStatusFixModal = ({ onUpdateComplete }: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [targetSettlementId, setTargetSettlementId] = useState<string | null>(
		null
	)
	const [targetYearMonth, setTargetYearMonth] = useState<{
		year: number
		month: number
	} | null>(null)
	const [statusChanges, setStatusChanges] = useState<
		Map<string, RereservationStatusInput>
	>(new Map())
	const [error, setError] = useState<string | null>(null)
	const [showToast, setShowToast] = useState(false)

	const { data: rereservationsData, loading: rereservationsLoading } =
		useCompanyAdminQuery(GetSettlementRereservations, {
			variables: {
				param: { settlementId: targetSettlementId ?? "" },
			},
			skip: !targetSettlementId || !isOpen,
			fetchPolicy: "network-only",
		})

	const [updateStatuses, { loading: updateLoading }] = useCompanyAdminMutation(
		ManuallyUpdateRereservationStatuses,
		{
			context: { disableErrorDialog: true },
		}
	)

	const rereservations = useMemo(
		() => rereservationsData?.settlementRereservations ?? [],
		[rereservationsData]
	)

	const changedCount = statusChanges.size

	const openModal = useCallback(
		(settlementId: string, yearMonth: { year: number; month: number }) => {
			setTargetSettlementId(settlementId)
			setTargetYearMonth(yearMonth)
			setStatusChanges(new Map())
			setError(null)
			setIsOpen(true)
		},
		[]
	)

	/** cancelId → クエリから取得した初期ステータス */
	const initialStatusMap = useMemo(() => {
		const m = new Map<string, RereservationStatusInput>()
		for (const r of rereservations) {
			m.set(r.cancelId, toStatusInput(r.status))
		}
		return m
	}, [rereservations])

	const handleStatusChange = useCallback(
		(cancelId: string, newStatus: RereservationStatusInput) => {
			setStatusChanges((prev) => {
				const next = new Map(prev)
				const initial = initialStatusMap.get(cancelId)
				if (newStatus === initial) {
					next.delete(cancelId)
				} else {
					next.set(cancelId, newStatus)
				}
				return next
			})
		},
		[initialStatusMap]
	)

	const handleSubmit = useCallback(async () => {
		if (!targetSettlementId || changedCount === 0) return
		setError(null)

		const items = Array.from(statusChanges.entries()).map(
			([cancelId, newStatus]) => ({
				cancelId,
				newStatus,
			})
		)

		try {
			await updateStatuses({
				variables: {
					input: {
						settlementId: targetSettlementId,
						items,
					},
				},
			})
			setIsOpen(false)
			onUpdateComplete()
			// トースト通知
			setShowToast(true)
		} catch (e) {
			const msg =
				e instanceof Error ? e.message : "ステータスの更新に失敗しました。"
			setError(msg)
			console.error("manuallyUpdateRereservationStatuses error:", e)
		}
	}, [
		targetSettlementId,
		changedCount,
		statusChanges,
		updateStatuses,
		onUpdateComplete,
	])

	// Toast auto-hide
	useEffect(() => {
		if (!showToast) return
		const timer = setTimeout(() => setShowToast(false), 5000)
		return () => clearTimeout(timer)
	}, [showToast])

	const render = useCallback(() => {
		return (
			<>
				<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
					<div className={styles.modal_content}>
						<h2 className={styles.title}>
							再予約ステータス修正
							{targetYearMonth && ` — ${formatYearMonth(targetYearMonth)}`}
						</h2>

						{error && <p className={styles.error}>{error}</p>}

						{rereservationsLoading ? (
							<div className={styles.loading}>読み込み中...</div>
						) : (
							<div className={styles.table_container}>
								<table className={styles.table}>
									<thead>
										<tr>
											<th>顧客名</th>
											<th>スタッフ名</th>
											<th>来店日時</th>
											<th>金額</th>
											<th>修正後ステータス</th>
										</tr>
									</thead>
									<tbody>
										{rereservations.map((r) => {
											const hasChange = statusChanges.has(r.cancelId)
											const currentStatus =
												statusChanges.get(r.cancelId) ?? toStatusInput(r.status)
											const statusColorClass =
												currentStatus === "VISITED"
													? styles.status_visited
													: currentStatus === "CANCELLED"
														? styles.status_cancelled
														: styles.status_unconfirmed
											return (
												<tr key={r.cancelId}>
													<td>{r.customerName}</td>
													<td>{r.staffName ?? "-"}</td>
													<td>{formatIsoDateTime(r.startTime)}</td>
													<td className={styles.amount_cell}>
														{formatAmount(r.amount)}
													</td>
													<td>
														<select
															className={`${styles.status_select} ${statusColorClass} ${hasChange ? styles.changed : ""}`}
															value={currentStatus}
															onChange={(e) =>
																handleStatusChange(
																	r.cancelId,
																	e.target.value as RereservationStatusInput
																)
															}
														>
															{statusOptions.map((opt) => (
																<option key={opt.value} value={opt.value}>
																	{opt.label}
																</option>
															))}
														</select>
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						)}

						<div className={styles.footer}>
							<span className={styles.change_count}>
								{rereservations.length}件中 {changedCount}件変更済み
							</span>
							<div className={styles.button_group}>
								<button
									type="button"
									className={styles.cancel_button}
									onClick={() => setIsOpen(false)}
									disabled={updateLoading}
								>
									キャンセル
								</button>
								<button
									type="button"
									className={styles.submit_button}
									onClick={handleSubmit}
									disabled={updateLoading || changedCount === 0}
								>
									{updateLoading ? "処理中..." : "ステータスを変更"}
								</button>
							</div>
						</div>
					</div>
				</ModalDialog>

				{showToast && (
					<div className={styles.toast}>
						<p>
							ステータスを更新しました。明細、再予約ステータス一覧は数秒後に自動で再作成されます。
						</p>
						<p>数秒後にリロードしてください。</p>
					</div>
				)}
			</>
		)
	}, [
		isOpen,
		targetYearMonth,
		error,
		rereservationsLoading,
		rereservations,
		statusChanges,
		changedCount,
		updateLoading,
		handleStatusChange,
		handleSubmit,
		showToast,
	])

	return {
		render,
		openModal,
	}
}
